import React from 'react';
import {
  UNSAFE_DataRouterContext,
  useHref,
  useLocation,
  useNavigate,
  useNavigation,
} from 'react-router-dom';

import { performCanonicalScrollContinuation } from './canonicalReplace';
import { useOptionalNavigationHistory } from './NavigationHistoryProvider';

const READ_FIELD = Symbol('read-navigation-query-field');
const WRITE_FIELD = Symbol('write-navigation-query-field');

const BLOCKED_FIELD_TOKENS = new Set([
  'password',
  'token',
  'secret',
  'card',
  'cvv',
  'blob',
  'file',
  'filename',
  'latitude',
  'longitude',
  'media',
  'recording',
]);
const RESERVED_SCHEMA_FIELD_NAMES = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

const STRICT_DECIMAL_INTEGER = /^-?(?:0|[1-9]\d*)$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface NavigationQueryField<T> {
  readonly key: string;
  readonly defaultValue: Readonly<T>;
}

interface InternalNavigationQueryField<T> extends NavigationQueryField<T> {
  readonly [READ_FIELD]: (values: readonly string[]) => T;
  readonly [WRITE_FIELD]: (value: unknown) => readonly string[];
}

export interface NavigationQuerySchema<T extends Record<string, unknown>> {
  readonly fields: { readonly [K in keyof T]: NavigationQueryField<T[K]> };
  readonly fieldNames: readonly Extract<keyof T, string>[];
}

export type NavigationQueryPatch<T extends Record<string, unknown>> = Partial<T>;

export interface NavigationQueryUpdateOptions<T extends Record<string, unknown>> {
  canonical?: boolean;
  reset?: readonly (keyof T)[];
  state?: unknown;
}

interface StringFieldOptions {
  defaultValue?: string;
  maxLength?: number;
}

interface IntegerFieldOptions {
  defaultValue: number;
  min?: number;
  max?: number;
  step?: number;
}

interface BooleanFieldOptions {
  defaultValue?: boolean;
}

interface StringListFieldOptions {
  defaultValue?: readonly string[];
  allowedValues?: readonly string[];
  maxItems?: number;
  maxItemLength?: number;
}

type ValuesForFields<TFields extends Record<string, NavigationQueryField<unknown>>> = {
  -readonly [K in keyof TFields]: TFields[K] extends NavigationQueryField<infer TValue>
    ? TValue
    : never;
};

function createField<T>(
  key: string,
  defaultValue: Readonly<T>,
  read: (values: readonly string[]) => T,
  write: (value: unknown) => readonly string[],
): NavigationQueryField<T> {
  if (key.length === 0) {
    throw new Error('Navigation query URL keys must not be empty');
  }
  return Object.freeze({
    key,
    defaultValue,
    [READ_FIELD]: read,
    [WRITE_FIELD]: write,
  }) as InternalNavigationQueryField<T>;
}

function isInternalField(
  field: NavigationQueryField<unknown>,
): field is InternalNavigationQueryField<unknown> {
  const candidate = field as Partial<InternalNavigationQueryField<unknown>>;
  return typeof candidate[READ_FIELD] === 'function'
    && typeof candidate[WRITE_FIELD] === 'function';
}

function normalizeStringList(values: readonly string[]): string[] {
  const normalized = new Set<string>();
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed.length > 0) normalized.add(trimmed);
  }
  return [...normalized].sort();
}

function sameStringList(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length
    && left.every((value, index) => value === right[index]);
}

function parseStrictInteger(value: string | undefined): number | null {
  if (value === undefined || !STRICT_DECIMAL_INTEGER.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function isRoundTripIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime())
    && parsed.toISOString().slice(0, 10) === value;
}

function stringField(
  key: string,
  options: StringFieldOptions = {},
): NavigationQueryField<string> {
  const defaultValue = options.defaultValue ?? '';
  const maxLength = options.maxLength ?? Number.POSITIVE_INFINITY;
  if (maxLength !== Number.POSITIVE_INFINITY
    && (!Number.isSafeInteger(maxLength) || maxLength < 0)) {
    throw new Error('Navigation query string maxLength must be a non-negative safe integer');
  }
  if (defaultValue.length > maxLength) {
    throw new Error('Navigation query string defaultValue exceeds maxLength');
  }
  return createField(
    key,
    defaultValue,
    (values) => {
      const value = values[0];
      return value !== undefined && value.length <= maxLength ? value : defaultValue;
    },
    (value) => typeof value === 'string'
      && value.length <= maxLength
      && value !== defaultValue
      ? [value]
      : [],
  );
}

function integerField(
  key: string,
  options: IntegerFieldOptions,
): NavigationQueryField<number> {
  const defaultValue = options.defaultValue;
  const min = options.min ?? Number.MIN_SAFE_INTEGER;
  const max = options.max ?? Number.MAX_SAFE_INTEGER;
  const step = options.step ?? 1;
  const stepAnchor = options.min ?? defaultValue;
  if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max) || min > max) {
    throw new Error('Navigation query integer bounds must be ordered safe integers');
  }
  if (!Number.isSafeInteger(step) || step <= 0) {
    throw new Error('Navigation query integer step must be a positive safe integer');
  }
  const isStepAligned = (value: number): boolean => (
    (BigInt(value) - BigInt(stepAnchor)) % BigInt(step) === 0n
  );
  if (!Number.isSafeInteger(defaultValue)
    || defaultValue < min
    || defaultValue > max
    || !isStepAligned(defaultValue)) {
    throw new Error('Navigation query integer defaultValue must be within its bounds');
  }
  const isValid = (value: unknown): value is number => typeof value === 'number'
    && Number.isSafeInteger(value)
    && value >= min
    && value <= max
    && isStepAligned(value);
  return createField(
    key,
    defaultValue,
    (values) => {
      const parsed = parseStrictInteger(values[0]);
      return parsed !== null && isValid(parsed) ? parsed : defaultValue;
    },
    (value) => isValid(value) && value !== defaultValue ? [String(value)] : [],
  );
}

function idField(key: string): NavigationQueryField<number | null> {
  return createField(
    key,
    null,
    (values) => {
      const parsed = parseStrictInteger(values[0]);
      return parsed !== null && parsed > 0 ? parsed : null;
    },
    (value) => typeof value === 'number'
      && Number.isSafeInteger(value)
      && value > 0
      ? [String(value)]
      : [],
  );
}

function enumField<const TValues extends readonly string[]>(
  key: string,
  values: TValues,
  defaultValue: TValues[number],
): NavigationQueryField<TValues[number]> {
  const allowed = new Set<string>(values);
  if (values.length === 0 || allowed.size !== values.length) {
    throw new Error('Navigation query enum values must be non-empty and unique');
  }
  if (!allowed.has(defaultValue)) {
    throw new Error('Navigation query enum defaultValue must be an allowed value');
  }
  return createField(
    key,
    defaultValue,
    (rawValues) => {
      const value = rawValues[0];
      return value !== undefined && allowed.has(value)
        ? value as TValues[number]
        : defaultValue;
    },
    (value) => typeof value === 'string'
      && allowed.has(value)
      && value !== defaultValue
      ? [value]
      : [],
  );
}

function booleanField(
  key: string,
  options: BooleanFieldOptions | boolean = {},
): NavigationQueryField<boolean> {
  const defaultValue = typeof options === 'boolean'
    ? options
    : options.defaultValue ?? false;
  return createField(
    key,
    defaultValue,
    (values) => {
      if (values[0] === '1') return true;
      if (values[0] === '0') return false;
      return defaultValue;
    },
    (value) => typeof value === 'boolean' && value !== defaultValue
      ? [value ? '1' : '0']
      : [],
  );
}

function dateField(key: string): NavigationQueryField<string | null> {
  return createField(
    key,
    null,
    (values) => isRoundTripIsoDate(values[0]) ? values[0] : null,
    (value) => isRoundTripIsoDate(value) ? [value] : [],
  );
}

function stringListField(
  key: string,
  options: StringListFieldOptions = {},
): NavigationQueryField<string[]> {
  if ((options.defaultValue?.length ?? 0) > 0) {
    throw new Error('Navigation query string-list defaultValue must be empty');
  }
  const defaultValue: readonly string[] = Object.freeze([]);
  const allowedValues = options.allowedValues
    ? new Set(options.allowedValues)
    : null;
  const maxItems = options.maxItems ?? Number.POSITIVE_INFINITY;
  const maxItemLength = options.maxItemLength ?? Number.POSITIVE_INFINITY;
  if (maxItems !== Number.POSITIVE_INFINITY
    && (!Number.isSafeInteger(maxItems) || maxItems < 0)) {
    throw new Error('Navigation query string-list maxItems must be a non-negative safe integer');
  }
  if (maxItemLength !== Number.POSITIVE_INFINITY
    && (!Number.isSafeInteger(maxItemLength) || maxItemLength < 0)) {
    throw new Error('Navigation query string-list maxItemLength must be a non-negative safe integer');
  }
  const normalize = (values: readonly string[]): string[] => normalizeStringList(values)
    .filter((value) => value.length <= maxItemLength && (allowedValues?.has(value) ?? true))
    .slice(0, maxItems);
  return createField<string[]>(
    key,
    defaultValue,
    (values) => {
      if (values.length === 0) return [...defaultValue];
      const normalized = normalize(values);
      return normalized.length > 0 ? normalized : [...defaultValue];
    },
    (value) => {
      if (!Array.isArray(value)) return [];
      const strings: string[] = [];
      for (const item of value as unknown[]) {
        if (typeof item !== 'string') return [];
        strings.push(item);
      }
      const normalized = normalize(strings);
      return sameStringList(normalized, defaultValue) ? [] : normalized;
    },
  );
}

export const navigationQuery = Object.freeze({
  string: stringField,
  integer: integerField,
  id: idField,
  enum: enumField,
  boolean: booleanField,
  date: dateField,
  stringList: stringListField,
});

function tokenizeFieldName(name: string): string[] {
  return name
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[_.\-\s]+/)
    .map((token) => token.toLowerCase())
    .filter((token) => token.length > 0);
}

export function isBlockedNavigationFieldName(name: string): boolean {
  return tokenizeFieldName(name).some((token) => BLOCKED_FIELD_TOKENS.has(token));
}

export function defineNavigationQuerySchema<
  const TFields extends Record<string, NavigationQueryField<unknown>>,
>(fields: TFields): NavigationQuerySchema<ValuesForFields<TFields>> {
  const fieldsPrototype = Object.getPrototypeOf(fields) as unknown;
  if (fieldsPrototype !== null && fieldsPrototype !== Object.prototype) {
    throw new Error('Reserved navigation query schema property: __proto__');
  }
  const fieldNames = Object.keys(fields) as Extract<keyof TFields, string>[];
  const ownedKeys = new Set<string>();
  for (const fieldName of fieldNames) {
    if (RESERVED_SCHEMA_FIELD_NAMES.has(fieldName)) {
      throw new Error(`Reserved navigation query schema property: ${fieldName}`);
    }
    const field = fields[fieldName];
    if (!field || !isInternalField(field)) {
      throw new Error(`Navigation query field "${fieldName}" must use navigationQuery codecs`);
    }
    if (isBlockedNavigationFieldName(fieldName)) {
      throw new Error(`Blocked navigation query schema field name: ${fieldName}`);
    }
    if (isBlockedNavigationFieldName(field.key)) {
      throw new Error(`Blocked navigation query URL key: ${field.key}`);
    }
    if (ownedKeys.has(field.key)) {
      throw new Error(`Duplicate navigation query URL key: ${field.key}`);
    }
    ownedKeys.add(field.key);
  }
  return Object.freeze({
    fields: Object.freeze({ ...fields }),
    fieldNames: Object.freeze(fieldNames),
  }) as NavigationQuerySchema<ValuesForFields<TFields>>;
}

function getInternalField<T extends Record<string, unknown>>(
  schema: NavigationQuerySchema<T>,
  fieldName: Extract<keyof T, string>,
): InternalNavigationQueryField<unknown> {
  return schema.fields[fieldName] as InternalNavigationQueryField<unknown>;
}

function serializeNavigationQuery<T extends Record<string, unknown>>(
  schema: NavigationQuerySchema<T>,
  sourceSearch: string,
  values: T,
): string {
  const sourcePairs = Array.from(new URLSearchParams(sourceSearch).entries());
  const ownedKeys = new Set(
    schema.fieldNames.map((fieldName) => getInternalField(schema, fieldName).key),
  );
  const firstOwnedPairIndex = sourcePairs.findIndex(([key]) => ownedKeys.has(key));
  const foreignPairs = sourcePairs.filter(([key]) => !ownedKeys.has(key));
  const insertionIndex = firstOwnedPairIndex === -1
    ? foreignPairs.length
    : sourcePairs
      .slice(0, firstOwnedPairIndex)
      .filter(([key]) => !ownedKeys.has(key)).length;
  const ownedPairs: [string, string][] = [];
  for (const fieldName of schema.fieldNames) {
    const field = getInternalField(schema, fieldName);
    for (const value of field[WRITE_FIELD](values[fieldName])) {
      ownedPairs.push([field.key, value]);
    }
  }
  foreignPairs.splice(insertionIndex, 0, ...ownedPairs);
  const params = new URLSearchParams(foreignPairs);
  const serialized = params.toString();
  return serialized.length > 0 ? `?${serialized}` : '';
}

function readNavigationQuery<T extends Record<string, unknown>>(
  schema: NavigationQuerySchema<T>,
  search: string,
): { values: T; canonicalSearch: string } {
  const params = new URLSearchParams(search);
  const values = {} as T;
  for (const fieldName of schema.fieldNames) {
    const field = getInternalField(schema, fieldName);
    values[fieldName] = field[READ_FIELD](params.getAll(field.key)) as T[typeof fieldName];
  }
  return {
    values,
    canonicalSearch: serializeNavigationQuery(schema, search, values),
  };
}

function writeNavigationQuery<T extends Record<string, unknown>>(
  schema: NavigationQuerySchema<T>,
  sourceSearch: string,
  patch: NavigationQueryPatch<T>,
  reset: readonly (keyof T)[] | undefined,
): string {
  const values = { ...readNavigationQuery(schema, sourceSearch).values } as T;
  const resetFields = new Set<keyof T>(reset ?? []);
  for (const fieldName of schema.fieldNames) {
    const field = getInternalField(schema, fieldName);
    if (resetFields.has(fieldName)) {
      values[fieldName] = field.defaultValue as T[typeof fieldName];
    }
    if (Object.prototype.hasOwnProperty.call(patch, fieldName)) {
      values[fieldName] = patch[fieldName] as T[typeof fieldName];
    }
  }
  return serializeNavigationQuery(schema, sourceSearch, values);
}

export function useNavigationQuery<T extends Record<string, unknown>>(
  schema: NavigationQuerySchema<T>,
) {
  const location = useLocation();
  const documentPathname = useHref({ pathname: location.pathname });
  const navigate = useNavigate();
  const navigation = useNavigation();
  const history = useOptionalNavigationHistory();
  const navigationScopeId = history?.scopeId;
  const hasPendingNavigation = navigation.state !== 'idle'
    || navigation.location !== undefined;
  const router = React.useContext(UNSAFE_DataRouterContext)?.router;
  const cleanupTokenRef = React.useRef<string | null>(null);
  const parsed = React.useMemo(
    () => readNavigationQuery(schema, location.search),
    [location.search, schema],
  );

  React.useEffect(() => {
    if (parsed.canonicalSearch === location.search) {
      cleanupTokenRef.current = null;
      return;
    }
    const liveNavigation = router?.state.navigation;
    const hasLivePendingNavigation = liveNavigation !== undefined
      && (liveNavigation.state !== 'idle' || liveNavigation.location !== undefined);
    if (hasPendingNavigation || hasLivePendingNavigation) return;
    if (router && router.state.location.key !== location.key) return;
    const cleanupToken = JSON.stringify([
      location.key,
      location.search,
      parsed.canonicalSearch,
    ]);
    if (cleanupTokenRef.current === cleanupToken) return;
    cleanupTokenRef.current = cleanupToken;
    const target = {
      pathname: location.pathname,
      search: parsed.canonicalSearch,
      hash: location.hash,
    };
    if (navigationScopeId) {
      performCanonicalScrollContinuation({
        scopeId: navigationScopeId,
        fromLocationKey: location.key,
        ...target,
      }, () => navigate(target, {
        replace: true,
        state: location.state,
      }));
      return;
    }
    void navigate(target, {
      replace: true,
      state: location.state,
    });
  }, [
    hasPendingNavigation,
    navigationScopeId,
    location.hash,
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigate,
    parsed.canonicalSearch,
    router,
  ]);

  const update = React.useCallback((
    patch: NavigationQueryPatch<T>,
    replace: boolean,
    options?: NavigationQueryUpdateOptions<T>,
  ) => {
    const search = writeNavigationQuery(schema, location.search, patch, options?.reset);
    const sourceState = options?.state ?? location.state;
    if (search === location.search && sourceState === location.state) return;
    const state = sourceState;
    const target = {
      pathname: location.pathname,
      search,
      hash: location.hash,
    };
    const navigationOptions = {
      replace,
      state,
    };
    if (replace && options?.canonical && navigationScopeId) {
      performCanonicalScrollContinuation({
        scopeId: navigationScopeId,
        fromLocationKey: location.key,
        ...target,
      }, () => navigate(target, navigationOptions));
      return;
    }
    void navigate(target, navigationOptions);
  }, [
    location.hash,
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigationScopeId,
    navigate,
    schema,
  ]);

  const commit = React.useCallback((
    patch: NavigationQueryPatch<T>,
    options?: NavigationQueryUpdateOptions<T>,
  ) => update(patch, false, options), [update]);
  const replaceDraft = React.useCallback((
    patch: NavigationQueryPatch<T>,
    options?: NavigationQueryUpdateOptions<T>,
  ) => update(patch, true, options), [update]);
  const href = React.useCallback((
    patch: NavigationQueryPatch<T>,
    options?: Pick<NavigationQueryUpdateOptions<T>, 'reset'>,
  ) => `${documentPathname}${writeNavigationQuery(
    schema,
    location.search,
    patch,
    options?.reset,
  )}${location.hash}`, [
    documentPathname,
    location.hash,
    location.search,
    schema,
  ]);

  return React.useMemo(() => ({
    values: parsed.values,
    commit,
    replaceDraft,
    href,
  }), [commit, href, parsed.values, replaceDraft]);
}
